import { Application, BasePlugin } from '@goodboydigital/astro';
import { Signal } from 'typed-signals';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { CardOpts } from '../ui/Card';
import { Random } from '../utils/Random';

const defaultData = {
    dailyRewardNext: 0,
    playtimeRewardNext: 0,
    playtimeRewardCountdown: -1,
};

type RewardPersistentData = typeof defaultData;

export interface RewardItem
{
    id: string,
    value: number,
    odds: number
}

export interface RewardsConfig
{
    preRacePerks: RewardItem[],
    postRacePerks: RewardItem[],
    dailyRewards: RewardItem[],
    playtimeRewards: RewardItem[]
}

export class RewardPlugin implements BasePlugin
{
    public app: App;
    public onRewardUpdate: Signal<() => void>;
    private persistentData: RewardPersistentData;
    private accumulator = 0;
    private config!: RewardsConfig;

    constructor(app: Application)
    {
        this.app = app as App;
        this.persistentData = { ...defaultData };
        this.onRewardUpdate = new Signal();
    }

    public start(): void
    {
        this.config = this.app.assets.getData('rewards');
        this.loadPersistentData();
        this.resetPlaytimeReward();
    }

    private loadPersistentData(): void
    {
        this.persistentData = { ...defaultData };

        const storedData = (this.app.storage.getObject('rewards') || {}) as Partial<RewardPersistentData>;

        Object.assign(this.persistentData, storedData);
    }

    private savePersistentData(): void
    {
        this.app.storage.setItem('rewards', this.persistentData);
    }

    public update(delta = 1): void
    {
        this.updatePlaytimeReward(delta);
    }

    // === PLAYTIME REWARD ============================================================================================

    private getPlaytimeRewardTime(): number
    {
        return 1000 * 60 * 5;
    }

    private resetPlaytimeReward(): void
    {
        if (this.now() > this.persistentData.playtimeRewardNext && this.persistentData.playtimeRewardCountdown === -1)
        {
            this.persistentData.playtimeRewardCountdown = this.getPlaytimeRewardTime();
            this.savePersistentData();
            this.onRewardUpdate.emit();
        }
    }

    private updatePlaytimeReward(delta = 1): void
    {
        if (this.persistentData.playtimeRewardCountdown <= 0) return;

        this.accumulator += 16.6666 * delta;
        if (this.accumulator >= 1000)
        {
            this.persistentData.playtimeRewardCountdown -= 1000;
            if (this.persistentData.playtimeRewardCountdown <= 0) this.persistentData.playtimeRewardCountdown = 0;

            // throtle save to every 10 seconds
            if (this.persistentData.playtimeRewardCountdown % 10000 === 0) this.savePersistentData();

            this.accumulator = 0;
            this.onRewardUpdate.emit();
        }
    }

    public canClaimPlaytimeReward(): boolean
    {
        return this.now() > this.persistentData.playtimeRewardNext && this.persistentData.playtimeRewardCountdown === 0;
    }

    public isCountingDownPlaytimeReward(): boolean
    {
        return this.persistentData.playtimeRewardCountdown > 0;
    }
    
    public getPlaytimeRewards(): RewardItem[]
    {
        return this.config.playtimeRewards;
    }

    public async claimPlaytimeReward(reward: RewardItem, showAd: boolean): Promise<void>
    {
        if (!this.canClaimPlaytimeReward()) return;

        // Daily rewards only available in ios/android apps
        if (!Settings.isApp() && !Settings.browserRewards)
        {
            this.app.popup.presentDownloadPopup();

            return;
        }

        this.persistentData.playtimeRewardCountdown = -1;
        
        if(showAd){
            await this.app.ads.playRewardedVideo(() => this.onRewardVideoReward(reward));
        }
        else{
            this.reward(reward);
        }
        
        this.onRewardUpdate.emit();

        this.resetPlaytimeReward();
        
    }

    public getPlaytimeRweardCountdown(): number
    {
        return this.persistentData.playtimeRewardCountdown;
    }

    // === DAILY REWARD ===============================================================================================

    public canClaimDailyReward(): boolean
    {
        return Settings.overrideDailyRewardClaim || this.now() > this.persistentData.dailyRewardNext;
    }
    
    public getDailyRewards(): RewardItem[]
    {
        return this.config.dailyRewards;
    }

    public async claimDailyReward(reward: RewardItem, showAd: boolean): Promise<void>
    {
        if (!this.canClaimDailyReward()) return;

        // Daily rewards only available in ios/android apps
        if (!Settings.isApp() && !Settings.browserRewards)
        {
            this.app.popup.presentDownloadPopup();

            return;
        }

        this.persistentData.dailyRewardNext = this.tomorrow();
        this.savePersistentData();
        
        if(showAd){
            await this.app.ads.playRewardedVideo(() => this.onRewardVideoReward(reward));
        }
        else{
            this.reward(reward);
        }
        
        this.onRewardUpdate.emit();

    }
    
    private onRewardVideoReward(reward: RewardItem): void
    {
        this.reward(reward);
    }

    // === PRE-RACE PERKS =============================================================================================

    public canClaimPreRacePerk(): boolean
    {
        return true;
    }
    
    public getPreRacePerks(): RewardItem[]
    {
        return this.config.preRacePerks;
    }

    public async claimPreRacePerk(reward: RewardItem, showAd: boolean): Promise<void>
    {
        if (!this.canClaimPreRacePerk()) return;

        // Pre race perks only available in ios/android apps
        if (!Settings.isApp() && !Settings.browserRewards)
        {
            this.app.popup.presentDownloadPopup();

            return;
        }

        if(showAd){
            await this.app.ads.playRewardedVideo(() => this.onRewardVideoReward(reward));
        }
        else{
            this.reward(reward);
        }
    }

    // === POST-RACE PERKS =============================================================================================

    public canClaimPostRacePerk(): boolean
    {
        return !Settings.browserLite && Settings.isApp() !== undefined;
    }
    
    public getPostRacePerks(): RewardItem[]
    {
        return this.config.postRacePerks;
    }

    public async claimPostRacePerk(reward: RewardItem, showAd: boolean): Promise<void>
    {
        if (!this.canClaimPostRacePerk()) return;

        // Post race perks only available in ios/android apps
        if (!Settings.isApp() && !Settings.browserRewards)
        {
            this.app.popup.presentDownloadPopup();

            return;
        }
        
        if(showAd){
            await this.app.ads.playRewardedVideo(() => this.onRewardVideoReward(reward));
        }
        else{
            this.reward(reward);
        }
    }
    
    public getRandomReward(items: RewardItem[]): RewardItem
    {
        const oddsList = items.map((v) => [v.odds, v]) as [number, RewardItem][];
        const result = Random.pickByOdds<RewardItem>(oddsList);
        return result;
    }

    // === ACTUAL REWARDS =============================================================================================

    private claimRandomReward(items: RewardItem[]): void
    {
        const result = this.getRandomReward(items);

        this.reward(result);
    }
    
    private reward(item: RewardItem): void
    {
        if (item.id === 'coins') this.rewardCoins(item.value);
        if (item.id === 'diamonds') this.rewardDiamonds(item.value);
        if (item.id === 'flipSpeed') this.rewardRacePerk(item.id, item.value);
        if (item.id === 'maxSpeed') this.rewardRacePerk(item.id, item.value);
        if (item.id === 'trickBonus') this.rewardRacePerk(item.id, item.value);
        if (item.id === 'coinMultiplier')
        {
            const multiplier = Math.max(item.value - 1, 0);
            const extraCoins = Math.ceil(Shared.game.race.coins * multiplier);

            this.rewardCoins(extraCoins);
        }
    }

    private rewardRacePerk(type: string, value: number): void
    {
        console.log('[RewardsPlugin] Reward race perk:', type, 'value:', value);

        const cardOpts: Partial<CardOpts> = {};

        if (type === 'flipSpeed')
        {
            Shared.game.race.racePerkFlipSpeed = value;
            cardOpts.image = 'icon-replay';
            cardOpts.imageScale = 2;
        }
        if (type === 'maxSpeed')
        {
            Shared.game.race.racePerkMaxSpeed = value;
            cardOpts.image = 'icon-double-arrow';
            cardOpts.imageScale = 2;
        }
        if (type === 'trickBonus')
        {
            Shared.game.race.racePerkTrickBonus = value;
            Shared.game.race.racePerkMaxSpeed = value;
            cardOpts.image = 'icon-points';
            cardOpts.imageScale = 2;
        }
        this.app.popup.presentPrizePopup({
            title: `preRacePerk_${type}`,
            titleParams: { v: `x${value}` },
            cardOpts,
        });
    }

    private rewardCoins(numCoins: number): void
    {
        console.log('[RewardsPlugin] Reward coins:', numCoins);

        Shared.user.changeCoins(numCoins);
        this.app.popup.presentPrizePopup({
            title: 'prizePopup_coins',
            titleParams: { num: numCoins },
            cardOpts: {
                image: 'coins-small',
                imageScale: 4,
            },
        });
    }

    private rewardDiamonds(numDiamonds: number): void
    {
        console.log('[RewardsPlugin] Reward diamonds:', numDiamonds);

        Shared.user.changeDiamonds(numDiamonds);
        this.app.popup.presentPrizePopup({
            title: 'prizePopup_diamonds',
            titleParams: { num: numDiamonds },
            cardOpts: {
                image: 'diamond-large',
                imageScale: 1,
            },
        });
    }

    // === UTILITY ====================================================================================================

    private today(): number
    {
        const d = new Date();

        d.setHours(0, 0, 0, 0);

        return d.getTime();
    }

    private tomorrow(): number
    {
        return this.today() + (1000 * 60 * 60 * 24);
    }

    private now(): number
    {
        return new Date().getTime();
    }
}
